import React from 'react';
import { Form, Input, Button, Card, Typography, message, Divider, Tag } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { login } from '../../../redux/slices/authSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import styled from 'styled-components';

const { Title, Text } = Typography;

const TEST_CREDENTIALS = [
  {
    role: 'Admin',
    color: 'red',
    email: 'admin@gov.in',
    password: 'admin123',
  },
  {
    role: 'Representative',
    color: 'blue',
    email: 'atul.garg@sansad.nic.in',
    password: 'rep123',
  },
  {
    role: 'Citizen',
    color: 'green',
    email: 'amar@gmail.com',
    password: 'Amar@1234',
  },
];

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f0f2f5;
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 440px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const TestCredentialRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  margin-bottom: 8px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: #fafafa;
    border-color: #1677ff;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const Login = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading } = useAppSelector((state) => state.auth);

  const fillCredentials = (email, password) => {
    form.setFieldsValue({ email, password });
    message.info({ content: 'Test credentials filled. Click Login to continue.' });
  };

  const onFinish = async (values) => {
    try {
      const resultAction = await dispatch(login(values));
      if (login.fulfilled.match(resultAction)) {
        const { user } = resultAction.payload;
        // debugger;
        console.log('Logged in user role:', user.role); // Debug log
        message.success({ content: 'Login successful!' });
        switch (user.role) {
          case 'ADMIN':
            navigate('/admin/dashboard');
            break;
          case 'REPRESENTATIVE':
            navigate('/representative/dashboard');
            break;
          case 'CITIZEN':
            navigate('/citizen/dashboard');
            break;
          case 'VOLUNTEER':
            navigate('/volunteer/dashboard');
            break;
          default:
            message.error({ content: 'User role is undefined. Redirecting to login.' });
            navigate('/login');
            break;
        }
      } else if (login.rejected.match(resultAction)) {
        message.error({ 
          content: resultAction.payload?.toString() || 'Login failed. Please try again.'
        });
      }
    } catch (err) {
      message.error({ content: 'Login failed. Please try again.' });
    }
  };

  return (
    <Container>
      <StyledCard>
        <Title level={2} style={{ textAlign: 'center', marginBottom: 32 }}>
          Login to Uttardayitav
        </Title>
        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Email"
              size="large"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
            >
              Login
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            Don't have an account?{' '}
            <Link to="/register">Register now!</Link>
          </div>
        </Form>

        <Divider style={{ margin: '24px 0 16px' }}>Testing Phase</Divider>

        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
          Click a role below to fill the login form:
        </Text>

        {TEST_CREDENTIALS.map(({ role, color, email, password }) => (
          <TestCredentialRow
            key={role}
            role="button"
            tabIndex={0}
            onClick={() => fillCredentials(email, password)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fillCredentials(email, password);
              }
            }}
          >
            <div>
              <Tag color={color} style={{ marginRight: 0 }}>
                {role}
              </Tag>
            </div>
            <Text style={{ fontSize: 13 }}>
              <Text strong>Email:</Text> {email}
            </Text>
            <Text style={{ fontSize: 13 }}>
              <Text strong>Password:</Text> {password}
            </Text>
          </TestCredentialRow>
        ))}
      </StyledCard>
    </Container>
  );
};

export default Login;
